import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Check,
  ChevronsUpDown,
  ClipboardList,
  FileText,
  HelpCircle,
  Inbox,
  LayoutGrid,
  LogOut,
  Pin,
  PinOff,
  Plus,
  Search,
  Settings,
  X,
} from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';
import { setActiveProjectId } from '../../lib/activeProject';
import { formatPcoNumber } from '../../lib/format';
import { getRailPcoPins, toggleRailPcoPin } from '../../lib/railPcoPins';
import {
  recentRailPcos,
  type MeProjectMembership,
  type PcoStatus,
  type SettingsPco,
} from '@coffman-tm/shared';
import coffmanLogo from '../../assets/coffman-logo.png';
import coffmanIcon from '../../assets/coffman-icon.png';

/**
 * Side rail navigation (layout per Brandon's Claude-Design pick, 2026-07-10,
 * re-skinned in our tokens).
 *
 * Top to bottom: brand + pin toggle → search (Ctrl+K) → PDF Inbox → "Recent
 * PCOs" → All PCOs (flyout directory) → flexible space → New PCO CTA → PCO Log
 * / Project Settings / Help → user footer.
 *
 * ONE TREE, THREE WIDTHS. Every row is rendered in every state; the rail's
 * width (64px ↔ 280px, a CSS transition) is what reveals or clips it. There is
 * no collapsed markup and no expanded markup — labels live in the tree
 * permanently inside `.rail-label` spans and fade with the width, and the one
 * collapsed-only affordance (the compact search trigger) crossfades against its
 * expanded counterpart rather than swapping in.
 *
 * That is not a styling preference, it is what makes the mechanic work: two
 * subtrees have nothing to animate between, and swapping them destroys focus
 * and component instances mid-interaction (a menu opened from the rail gets
 * re-parented under the cursor). One tree means the browser animates a width,
 * and focus, scroll position, and any open menu survive by construction.
 *
 *   collapsed          64px icons; grid track 64px.
 *   hover-overlay      280px, `position: fixed` over the page — the grid track
 *                      stays 64px, so content never moves. Entered by hovering
 *                      (150ms) or focus landing inside; left on mouse-out
 *                      (300ms) or focus-out. Transient, never stored.
 *   pinned             280px with a 280px grid track: the content shifts. The
 *                      toggle pins/unpins and that choice persists
 *                      (`lib/railPref.ts`). A new browser starts collapsed.
 *
 * The nav is `position: fixed` in ALL three states and `main` is pinned to grid
 * column 2, so the rail's visual width and the gutter the content reserves are
 * independent by construction — `--rail-w` vs `--rail-gutter` in the CSS. They
 * agree except during a hover overlay, which is the whole trick.
 *
 * MENUS FREEZE THE RAIL. One rule, both flyouts (the project switcher and the
 * All-PCOs directory): a menu opens against whatever state the rail is in and
 * the rail holds that state until it closes — it neither expands nor collapses
 * underneath an open menu. On close, the normal hover/focus rules resume from
 * current reality. Both menus are positioned off the rail's width (the switcher
 * menu is `fixed` beside a collapsed rail, absolute inside `.rail-project` when
 * expanded; the flyout sits at `left: var(--rail-w)`), so the freeze is what
 * guarantees those offsets are only ever resolved at rest, never mid-animation.
 *
 * Search live-filters the active PCOs by number or name and swaps the Recent
 * list for matches while a query is present. "Recent" is genuinely recent
 * (activity in the last 4 weeks or newly created — recentRailPcos), no
 * filler; the currently-viewed PCO is pinned in if it didn't qualify.
 * Completed PCOs (paid/void) appear in neither — they live in the PCO Log's
 * filters.
 */
const ACTIVE_STATUSES: ReadonlySet<PcoStatus> = new Set([
  'open',
  'active',
  'pending_approval',
  'approved',
]);

const SEARCH_CAP = 20;

/** Pointer dwell before an unpinned rail expands. Short enough to feel like a
 *  hover, long enough that crossing the rail on the way somewhere else doesn't. */
const HOVER_ENTER_MS = 150;
/** Grace period after the pointer leaves — covers the diagonal slip off a nav
 *  item and a quick trip out to the flyout and back. */
const HOVER_LEAVE_MS = 300;

export interface SideRailProps {
  pcos: SettingsPco[];
  /** Number of PDFs sitting in the inbox (≥1 pending block). Badge on the tab. */
  inboxCount?: number;
  onCreatePco: () => void;
  /** Persisted choice: the rail is expanded AND holds a 280px grid column. */
  pinned: boolean;
  onTogglePin: () => void;
  /**
   * The rail's own width, reported up so the shell can carry it as a class.
   * Deliberately NOT "is it an overlay": the shell already knows whether it is
   * pinned, and the overlay is exactly the disagreement between the two. One
   * signal per number keeps the CSS from having to reason about states.
   */
  onNarrowChange: (narrow: boolean) => void;
}

export function SideRail({
  pcos,
  inboxCount,
  onCreatePco,
  pinned,
  onTogglePin,
  onNarrowChange,
}: SideRailProps) {
  const { me, signOut } = useAuth();
  const location = useLocation();
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const railRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // ── Expansion state ──────────────────────────────────────────────────────
  // Two independent "hold it open" reasons, either of which beats collapsed.
  // Keeping them separate is what makes the interactions compose: the pointer
  // can leave while focus stays, and neither stomps the other's bookkeeping.
  // An open menu is a third thing entirely — it freezes whatever these two
  // currently add up to, rather than voting.
  const [hoverOpen, setHoverOpen] = useState(false);
  const [focusOpen, setFocusOpen] = useState(false);
  const menuOpen = flyoutOpen || projectMenuOpen;

  const expanded = pinned || hoverOpen || focusOpen;
  const collapsed = !expanded;

  const enterTimer = useRef<number | null>(null);
  const leaveTimer = useRef<number | null>(null);
  /** `menuOpen` as of the last commit — timer callbacks close over stale state. */
  const menuOpenRef = useRef(false);
  /** Set when the user unpins with the pointer still over the rail: without it
   *  the rail would instantly re-expand on hover and the click would look inert. */
  const hoverSuppressed = useRef(false);
  /** Ctrl+K from a collapsed rail: expand first, focus once the input is no
   *  longer aria-hidden. See `focusSearch`. */
  const wantSearchFocus = useRef(false);

  // Layout effect, not a passive one: the rail's width comes off `--rail-w`,
  // which lives on the shell. A passive effect lands after paint — a frame of
  // the rail at the wrong width, with every label's reveal keyed off the same
  // class one frame behind the markup.
  useLayoutEffect(() => {
    onNarrowChange(collapsed);
  }, [collapsed, onNarrowChange]);

  useEffect(
    () => () => {
      clearTimer(enterTimer);
      clearTimer(leaveTimer);
    },
    [],
  );

  // A collapsed rail has no reachable search box, so it must not be sitting on
  // a live query — the Recent list would silently still be showing matches.
  useEffect(() => {
    if (collapsed) setQuery('');
  }, [collapsed]);

  /** Collapse after the leave delay — unless the pointer is back over the rail
   *  by then. The `:hover` re-check also covers the case where no mouseleave
   *  ever fired because the element under the cursor was simply unmounted. */
  const scheduleCollapse = useCallback(() => {
    clearTimer(enterTimer);
    clearTimer(leaveTimer);
    leaveTimer.current = window.setTimeout(() => {
      leaveTimer.current = null;
      if (menuOpenRef.current) return; // frozen — the thaw below re-runs this
      if (isHovered(railRef.current)) return;
      setHoverOpen(false);
    }, HOVER_LEAVE_MS);
  }, []);

  const handleMouseEnter = () => {
    clearTimer(leaveTimer);
    if (menuOpen || hoverSuppressed.current || hoverOpen) return;
    clearTimer(enterTimer);
    enterTimer.current = window.setTimeout(() => {
      enterTimer.current = null;
      setHoverOpen(true);
    }, HOVER_ENTER_MS);
  };

  const handleMouseLeave = () => {
    hoverSuppressed.current = false;
    scheduleCollapse();
  };

  // Freeze / thaw. A layout effect so the mirror is current before any timer can
  // fire, and so the freeze lands in the same commit the menu opened in.
  const menuWasOpen = useRef(false);
  useLayoutEffect(() => {
    menuOpenRef.current = menuOpen;
    if (menuOpen) {
      // Kill anything in flight that would change the rail's width under an
      // open menu — notably a hover-enter timer armed by the very pointer trip
      // that reached the trigger.
      clearTimer(enterTimer);
      clearTimer(leaveTimer);
      menuWasOpen.current = true;
      return;
    }
    if (!menuWasOpen.current) return;
    menuWasOpen.current = false;
    // Thaw: re-derive both holds from current reality rather than restoring
    // whatever they were before the freeze. Focus by inspection (a click on a
    // flyout link leaves it on `body`; a keyboard user is still in the rail),
    // hover via the usual :hover-guarded delay.
    const el = document.activeElement;
    setFocusOpen(
      !!el && el !== document.body && !!railRef.current?.contains(el) && isFocusVisible(el),
    );
    scheduleCollapse();
  }, [menuOpen, scheduleCollapse]);

  /**
   * Focus-within expansion. Gated on `:focus-visible` on purpose: a mouse click
   * inside the rail also focuses what it hits, and treating that as a hold
   * would leave the rail stuck open after the pointer walked away.
   */
  const handleFocusIn = (e: FocusEvent<HTMLElement>) => {
    if (!isFocusVisible(e.target)) return;
    clearTimer(leaveTimer);
    // Frozen: the thaw re-derives from `document.activeElement`, so a focus that
    // arrives during an open menu is still honoured — just not mid-menu.
    if (menuOpen) return;
    setFocusOpen(true);
  };

  const handleFocusOut = (e: FocusEvent<HTMLElement>) => {
    if (menuOpen) return; // frozen — the thaw re-derives this
    const next = e.relatedTarget;
    if (next instanceof Node && railRef.current?.contains(next)) return;
    setFocusOpen(false);
  };

  /** Expand and put the cursor in the search box. */
  const focusSearch = useCallback(() => {
    // Frozen, and expanding would re-style an open menu mid-flight. Ctrl+K
    // reaches here past the menu's click-catcher; an expanded rail is already
    // the right width, so only the collapsed case has to bail.
    if (collapsed && menuOpen) return;
    if (!collapsed) {
      searchRef.current?.focus();
      return;
    }
    // Collapsed, the input is still carrying aria-hidden + tabindex="-1" in the
    // live DOM — React flips those on the next commit. Focusing an aria-hidden
    // element is invalid even for a single frame, so expand now and take focus
    // in the layout effect below, once the attributes are gone.
    wantSearchFocus.current = true;
    setFocusOpen(true);
  }, [collapsed, menuOpen]);

  useLayoutEffect(() => {
    if (collapsed || !wantSearchFocus.current) return;
    wantSearchFocus.current = false;
    searchRef.current?.focus();
  }, [collapsed]);

  const handleTogglePin = () => {
    // The freeze covers this control too — pinning IS a width change, and the
    // rail's width is held for as long as a menu is open. A pointer user cannot
    // reach the button (the menu's click-catcher eats the click), but a
    // keyboard user can still press Enter on it, and without this an unpin
    // would collapse the rail out from under the open menu and re-anchor it
    // mid-open. Inert is the honest answer: the menu is modal-ish, so nothing
    // outside it should act. Close it and the toggle works again.
    if (menuOpen) return;

    if (pinned && !isFocusVisible(toggleRef.current)) {
      // Pointer unpin: the user asked for collapsed and must see collapsed even
      // with the cursor parked on the button. (Keyboard unpin needs no special
      // case — focus stays in the rail, focus-within keeps it open as an
      // overlay, and only the content gutter moves.)
      //
      // Nothing to close here: the guard above means no menu is open, and
      // `projectMenuOpen` would be untouchable anyway — it mirrors state owned
      // by ProjectSwitcher, and clearing the mirror would desync it from a menu
      // still on screen.
      clearTimer(enterTimer);
      clearTimer(leaveTimer);
      hoverSuppressed.current = true;
      setHoverOpen(false);
      setFocusOpen(false);
    }
    onTogglePin();
  };

  const active = useMemo(
    () => pcos.filter((p) => ACTIVE_STATUSES.has(p.status)),
    [pcos],
  );
  // /pcos/:id and /pcos/:id/:week — NOT the bare /pcos log route.
  const currentPcoId = location.pathname.match(/^\/pcos\/([^/]+)/)?.[1] ?? null;

  // ── Pinned PCOs ──────────────────────────────────────────────────────────
  // Per-project, per-browser (localStorage — lib/railPcoPins.ts). `me` arrives
  // async, so the pins are re-read once the active project id resolves.
  const activeProjectId = me?.active_project_id ?? null;
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  useEffect(() => {
    setPinnedIds(getRailPcoPins(activeProjectId));
  }, [activeProjectId]);
  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);
  const togglePin = (pcoId: string) => {
    if (!activeProjectId) return;
    setPinnedIds(toggleRailPcoPin(activeProjectId, pcoId));
  };
  // Pinned PCOs LEAD the Recent list — no separate section (Brandon,
  // 2026-07-30: the pin's job is keeping a PCO in reach, not adding a group).
  // A pin OVERRIDES the lump-sum gate: that gate keeps the passive Recent
  // ranking honest ("no weekly work to be recent about"), but a pin is the PM
  // explicitly asking for the PCO on the rail — and on this project most of
  // the directory is lump-sum, which is exactly what gets pinned from the
  // All-PCOs flyout. A stale pin (paid/void/deleted PCO) matches nothing and
  // renders nowhere. Numeric order — a pinned set is a fixed shortlist, not
  // a ranking.
  const pinnedPcos = useMemo(
    () =>
      active
        .filter((p) => pinnedSet.has(p.id))
        .sort((a, b) => a.pco_number.localeCompare(b.pco_number, undefined, { numeric: true })),
    [active, pinnedSet],
  );

  // Genuinely recent only — no filler. The flyout is the full directory.
  // Pinned PCOs leave the Recent ranking entirely (they already lead the
  // list, and one PCO gets one row), and a pinned current PCO already has a
  // highlighted row, so it isn't re-appended here.
  const railPcos = recentRailPcos(
    active.filter((p) => !pinnedSet.has(p.id)),
    {
      nowMs: Date.now(),
      currentPcoId: currentPcoId && pinnedSet.has(currentPcoId) ? null : currentPcoId,
    },
  );
  // Same eligibility rule recentRailPcos applies, kept here only to tell the
  // two empty states apart ("nothing recent" vs "nothing rail-eligible").
  const railEligible = useMemo(
    () => active.filter((p) => p.billing_type !== 'lump_sum'),
    [active],
  );

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (q === '') return null;
    return active
      .filter(
        (p) =>
          p.pco_number.toLowerCase().includes(q) ||
          formatPcoNumber(p.pco_number).toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q),
      )
      .sort((a, b) => a.pco_number.localeCompare(b.pco_number, undefined, { numeric: true }));
  }, [active, q]);

  // Ctrl+K / Cmd+K focuses the search box from anywhere (expanding first if needed).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        focusSearch();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusSearch]);

  const clearSearch = () => setQuery('');
  const listed =
    matches === null ? [...pinnedPcos, ...railPcos] : matches.slice(0, SEARCH_CAP);
  const pinTitle = pinned ? 'Unpin navigation (collapse to icons)' : 'Pin navigation open';
  const pinLabel = pinned ? 'Unpin navigation' : 'Pin navigation open';

  return (
    <nav
      className="topnav"
      ref={railRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocusIn}
      onBlur={handleFocusOut}
    >
      <div className="brand">
        <img src={coffmanLogo} alt="Coffman Excavation" className="brand-logo" />
        <button
          ref={toggleRef}
          type="button"
          className="rail-collapse-btn"
          title={pinTitle}
          aria-label={pinLabel}
          aria-pressed={pinned}
          onClick={handleTogglePin}
        >
          {/* Collapsed, the top slot carries the Coffman mark instead of a
              bare pin glyph — the strip gets an identity, not just a control.
              The pin isn't lost: it swaps back in on hover/focus of this
              button (CSS, below), and the title + aria-label never changed,
              so the action stays announced to assistive tech either way.
              Nothing is hidden about state — a collapsed rail is by
              definition unpinned (`expanded = pinned || hover || focus`), so
              this glyph only ever means "pin it open". */}
          {collapsed && (
            <img src={coffmanIcon} alt="" className="rail-brand-icon" aria-hidden />
          )}
          <span className="rail-pin-glyph">
            {pinned ? (
              <PinOff size={15} strokeWidth={2} aria-hidden />
            ) : (
              <Pin size={15} strokeWidth={2} aria-hidden />
            )}
          </span>
        </button>
      </div>

      <ProjectSwitcher collapsed={collapsed} onOpenChange={setProjectMenuOpen} />

      {/* One control, two affordances: the input is the expanded rail's search
          box; the trigger is a transparent overlay button that owns the click
          (and the tab stop) while the rail is icons-only. Exactly one of them is
          reachable at a time, and the magnifier between them never moves. */}
      <div className="rail-search">
        <Search size={14} strokeWidth={2} aria-hidden className="rail-search-ico" />
        <input
          ref={searchRef}
          type="text"
          className="rail-search-input"
          placeholder="Search PCOs"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              clearSearch();
              e.currentTarget.blur();
            }
          }}
          aria-label="Search PCOs by number or name"
          aria-hidden={collapsed}
          tabIndex={collapsed ? -1 : undefined}
        />
        {/* Gated on `expanded`, not just on the query: collapsing clears the
            query a render later (a passive effect), and until it does this ✕
            would survive into the icon rail as a visible, tabbable button. */}
        {expanded && query !== '' ? (
          <button
            type="button"
            className="rail-search-clear"
            onClick={clearSearch}
            aria-label="Clear search"
          >
            <X size={13} strokeWidth={2} aria-hidden />
          </button>
        ) : (
          <span className="rail-search-kbd rail-label" aria-hidden>
            Ctrl K
          </span>
        )}
        <button
          type="button"
          className="rail-search-trigger"
          onClick={focusSearch}
          title="Search PCOs (Ctrl+K)"
          aria-label="Search PCOs"
          aria-hidden={expanded}
          tabIndex={expanded ? -1 : undefined}
        />
      </div>

      <ul className="nav-tabs">
        <NavTab
          to="/"
          label="PDF Inbox"
          icon={<Inbox size={16} />}
          end
          count={inboxCount}
          extraActiveMatch={(p) => p.startsWith('/inbox')}
        />
        {/* Reconciliation tab hidden 2026-07-10 (Brandon: the in-system flow
            covers it; bring back if Coffman asks). The /reconciliation route
            stays live for deep links. */}
      </ul>

      {/* Collapsed, this row's label fades out and its ::before rule fades in —
          the section break survives as the divider the icon rail needs. */}
      <div className="rail-eyebrow" aria-hidden={collapsed}>
        <FileText size={12} strokeWidth={2} aria-hidden className="rail-label" />
        <span className="rail-label">
          {matches === null ? 'Recent PCOs' : `Matches · ${matches.length}`}
        </span>
      </div>

      {listed.length === 0 ? (
        <div className="rail-note rail-label" aria-hidden={collapsed}>
          {matches !== null
            ? 'No PCOs match.'
            : active.length === 0
              ? 'No PCOs yet.'
              : // There ARE active PCOs but none reached the rail. Two very
                // different reasons, and blaming recency when the real cause is
                // that every PCO is lump-sum would send the PM hunting for
                // activity that was never going to show up here.
                railEligible.length === 0
                ? 'No weekly-rollup PCOs yet — lump-sum PCOs live in All PCOs.'
                : 'No activity in the last 4 weeks.'}
        </div>
      ) : (
        <ul className="rail-pco-list">
          {listed.map((p) => (
            <PcoRailItem
              key={p.id}
              pco={p}
              onNavigate={clearSearch}
              pinned={pinnedSet.has(p.id)}
              onTogglePin={() => togglePin(p.id)}
            />
          ))}
        </ul>
      )}
      {matches !== null && matches.length > SEARCH_CAP && (
        <div className="rail-note rail-label" aria-hidden={collapsed}>
          +{matches.length - SEARCH_CAP} more — keep typing to narrow.
        </div>
      )}

      {active.length > 0 && matches === null && (
        <button
          type="button"
          className="rail-allpcos"
          title={`All ${active.length} PCOs`}
          aria-label={`All ${active.length} PCOs`}
          aria-expanded={flyoutOpen}
          onClick={() => setFlyoutOpen((v) => !v)}
        >
          <span className="nav-icon" aria-hidden>
            <LayoutGrid size={16} />
          </span>
          <span className="rail-label">All PCOs</span>
          <span className="count rail-label">{active.length}</span>
        </button>
      )}

      {flyoutOpen && (
        <PcoFlyout
          pcos={active}
          currentPcoId={currentPcoId}
          pinnedSet={pinnedSet}
          onTogglePin={togglePin}
          onClose={() => setFlyoutOpen(false)}
        />
      )}

      <div className="rail-spacer" />

      <button type="button" className="btn btn-cta rail-cta" title="New PCO" onClick={onCreatePco}>
        <Plus size={15} strokeWidth={2.25} aria-hidden />
        <span className="rail-label">New PCO</span>
      </button>

      <ul className="nav-tabs">
        <NavTab to="/pcos" label="PCO Log" icon={<ClipboardList size={16} />} end />
        <NavTab to="/settings" label="Project Settings" icon={<Settings size={16} />} />
        <NavTab to="/help" label="Help" icon={<HelpCircle size={16} />} />
      </ul>

      {me && (
        <div
          className="rail-user"
          onClick={() => void signOut()}
          role="button"
          tabIndex={0}
          title="Sign out"
          aria-label={`${me.user.display_name} — sign out`}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void signOut();
          }}
        >
          <span className="avatar" aria-hidden>
            {initial(me.user.display_name)}
          </span>
          <span className="rail-user-id rail-label" aria-hidden={collapsed}>
            <span className="rail-user-name">{me.user.display_name}</span>
            <span className="rail-user-org">Coffman Excavation</span>
          </span>
          <LogOut size={14} strokeWidth={2} aria-hidden className="rail-user-out rail-label" />
        </div>
      )}
    </nav>
  );
}

function clearTimer(ref: MutableRefObject<number | null>): void {
  if (ref.current === null) return;
  window.clearTimeout(ref.current);
  ref.current = null;
}

/**
 * Both of these ask the browser a question that has no React equivalent, and
 * both are wrapped: the selector engine in a non-browser environment (the web
 * Vitest project runs in `node`) may not implement the pseudo-class, and a
 * throw here would take the rail down over a hover nicety.
 */
function isHovered(el: Element | null): boolean {
  try {
    return el?.matches(':hover') ?? false;
  } catch {
    return false;
  }
}

function isFocusVisible(el: EventTarget | null): boolean {
  if (!(el instanceof Element)) return false;
  try {
    return el.matches(':focus-visible');
  } catch {
    return false;
  }
}

/**
 * Which project am I looking at — always visible, never a guess.
 *
 * One membership: a quiet static badge (the number is still shown, because
 * "which project is this?" must be answerable without clicking). Two or more:
 * the same badge becomes a dropdown listing every membership.
 *
 * One control at both rail widths: the project NUMBER holds the icon slot, and
 * the eyebrow/name/chevron beside it fade like every other label. `collapsed`
 * decides where the open menu anchors and nothing else — beside a 64px rail it
 * is `fixed` (an absolute menu would be clipped by the rail's own overflow),
 * under a 280px rail it is absolute inside `.rail-project`.
 *
 * Switching writes the selection to localStorage and hard-navigates to "/".
 * A full reload is deliberate: every cached list, hook, and in-flight request
 * in this app is project-scoped, and re-mounting the whole tree is the only
 * cache-invalidation strategy with no surface area to get wrong.
 */
function ProjectSwitcher({
  collapsed,
  onOpenChange,
}: {
  /** Rail resting state — decides where the menu anchors, nothing else. */
  collapsed: boolean;
  /** The rail freezes its width while this menu is up — see SideRail's guard. */
  onOpenChange: (open: boolean) => void;
}) {
  const { me } = useAuth();
  const [open, setOpen] = useState(false);
  // Collapsed-rail menu only: the rail scrolls, so the trigger's viewport
  // position is not a constant. Measured at open rather than guessed from
  // --app-head-h, which drifts the moment the rail is scrolled.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuTop, setMenuTop] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Layout effect: the rail freezes its width on this signal, and a passive one
  // would land a frame late — long enough for a hover timer armed by the same
  // pointer trip to expand the rail under the menu that just opened.
  useLayoutEffect(() => {
    onOpenChange(open);
  }, [open, onOpenChange]);
  // Releases the rail's freeze if this switcher is unmounted while open rather
  // than closed.
  useLayoutEffect(
    () => () => {
      onOpenChange(false);
    },
    [onOpenChange],
  );

  const openMenu = () => {
    setMenuTop(triggerRef.current?.getBoundingClientRect().top ?? null);
    setOpen(true);
  };

  if (!me) return null;

  const memberships: MeProjectMembership[] = me.projects ?? [];
  const active =
    memberships.find((p) => p.project_id === me.active_project_id) ?? null;
  const number = active?.project_number ?? me.project.project_number;
  const name = active?.name ?? me.project.name;
  const multi = memberships.length > 1;
  const label = multi
    ? `Project ${number} — ${name} (switch project)`
    : `Project ${number} — ${name}`;

  const choose = (projectId: string) => {
    setOpen(false);
    if (projectId === me.active_project_id) return;
    setActiveProjectId(projectId);
    window.location.assign('/');
  };

  // The measured viewport anchor applies only to the collapsed rail's fixed
  // menu; expanded, the menu is absolutely positioned under the trigger and an
  // inline `top` would fight the stylesheet.
  const menuStyle: CSSProperties | null =
    collapsed && menuTop !== null ? { top: menuTop } : null;

  return (
    <div className="rail-project">
      {multi ? (
        <button
          ref={triggerRef}
          type="button"
          className="rail-project-btn"
          title={label}
          aria-expanded={open}
          aria-label={label}
          onClick={() => (open ? setOpen(false) : openMenu())}
        >
          <ProjectIdentity number={number} name={name} collapsed={collapsed} />
          <ChevronsUpDown size={14} strokeWidth={2} aria-hidden className="rail-project-chev" />
        </button>
      ) : (
        <div className="rail-project-btn is-static" title={label}>
          <ProjectIdentity number={number} name={name} collapsed={collapsed} />
        </div>
      )}
      {open && (
        <ProjectMenu
          memberships={memberships}
          activeId={me.active_project_id}
          onChoose={choose}
          onClose={() => setOpen(false)}
          {...(menuStyle === null ? {} : { style: menuStyle })}
        />
      )}
    </div>
  );
}

function ProjectIdentity({
  number,
  name,
  collapsed,
}: {
  number: string;
  name: string;
  collapsed: boolean;
}) {
  return (
    <>
      <span className="rail-project-num">{number}</span>
      <span className="rail-project-meta rail-label" aria-hidden={collapsed}>
        <span className="rail-project-eyebrow">Project</span>
        <span className="rail-project-name">{name}</span>
      </span>
    </>
  );
}

/**
 * Deliberately NOT role="menu"/"menuitem": that ARIA pattern promises arrow-key
 * roving focus and typeahead, which aren't implemented. Plain buttons in a
 * labelled container are honest — native tab order works, Escape closes, and
 * `aria-current` marks the active project. Adopt the menu roles only alongside
 * the keyboard behavior they advertise.
 */
function ProjectMenu({
  memberships,
  activeId,
  onChoose,
  onClose,
  style,
}: {
  memberships: MeProjectMembership[];
  activeId: string;
  onChoose: (projectId: string) => void;
  onClose: () => void;
  /** Viewport-anchored top for the collapsed rail's fixed menu. */
  style?: CSSProperties;
}) {
  return (
    <>
      <div className="flyout-catcher" onClick={onClose} aria-hidden />
      <div className="rail-project-menu" aria-label="Switch project" style={style}>
        {memberships.map((p) => (
          <button
            key={p.project_id}
            type="button"
            aria-current={p.project_id === activeId ? 'true' : undefined}
            className={p.project_id === activeId ? 'rail-project-opt active' : 'rail-project-opt'}
            onClick={() => onChoose(p.project_id)}
          >
            <span className="rail-project-opt-num">{p.project_number}</span>
            <span className="rail-project-opt-name">{p.name}</span>
            {p.project_id === activeId && (
              <Check size={13} strokeWidth={2.5} aria-hidden className="rail-project-opt-check" />
            )}
          </button>
        ))}
      </div>
    </>
  );
}

function NavTab({
  to,
  label,
  count,
  end,
  icon,
  extraActiveMatch,
}: {
  to: string;
  label: string;
  count?: number;
  end?: boolean;
  icon?: ReactNode;
  /**
   * Keep the tab highlighted on routes beyond its own `to` — e.g. PDF Inbox
   * (to="/") stays active on /inbox/:id, since that's still the Inbox section.
   */
  extraActiveMatch?: (pathname: string) => boolean;
}) {
  const location = useLocation();
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        title={label}
        className={({ isActive }) =>
          isActive || extraActiveMatch?.(location.pathname) ? 'active' : ''
        }
      >
        {icon && (
          <span className="nav-icon" aria-hidden>
            {icon}
          </span>
        )}
        {/* The label stays in the accessibility tree at both widths — it IS the
            link's name. Only its pixels are optional. */}
        <span className="rail-label">{label}</span>
        {typeof count === 'number' && count > 0 && <span className="count">{count}</span>}
      </NavLink>
    </li>
  );
}

/**
 * All-PCOs flyout — the full active directory in a panel beside the rail.
 * Every item is a direct link to its PCO page (one click, no log detour).
 * Closes on item click, outside click, or Escape.
 *
 * This is where pinning earns its keep (Brandon, 2026-07-30): the rail's
 * Recent list already surfaces active work, so the PCO worth pinning is
 * usually one only this directory shows — including lump-sum PCOs, which is
 * most of it. Every row gets the pin control: a pin overrides the rail's
 * lump-sum gate (an explicit ask beats a passive-ranking rule). Pinning here
 * does NOT close the flyout — the button is a sibling of the link, and the
 * PM may be pinning several.
 */
function PcoFlyout({
  pcos,
  currentPcoId,
  pinnedSet,
  onTogglePin,
  onClose,
}: {
  pcos: SettingsPco[];
  currentPcoId: string | null;
  pinnedSet: ReadonlySet<string>;
  onTogglePin: (pcoId: string) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sorted = [...pcos].sort((a, b) =>
    a.pco_number.localeCompare(b.pco_number, undefined, { numeric: true }),
  );

  return (
    <>
      <div className="flyout-catcher" onClick={onClose} aria-hidden />
      <div className="pco-flyout" role="dialog" aria-label="All PCOs">
        <div className="rail-eyebrow" style={{ padding: '14px 16px 6px' }}>
          All PCOs · {sorted.length}
        </div>
        <ul className="pco-flyout-list">
          {sorted.map((p) => {
            const num = formatPcoNumber(p.pco_number);
            const pinned = pinnedSet.has(p.id);
            return (
              <li key={p.id}>
                <NavLink
                  to={`/pcos/${p.id}`}
                  className={p.id === currentPcoId ? 'active' : ''}
                  onClick={onClose}
                  title={`${num} — ${p.description}`}
                >
                  <span className="pco-num">PCO {num}</span>
                  <span className="pco-desc">{p.description}</span>
                </NavLink>
                <button
                  type="button"
                  className={pinned ? 'rail-pin-toggle is-pinned' : 'rail-pin-toggle'}
                  title={pinned ? `Unpin PCO ${num}` : `Pin PCO ${num} to Recent PCOs`}
                  aria-label={pinned ? `Unpin PCO ${num}` : `Pin PCO ${num} to Recent PCOs`}
                  aria-pressed={pinned}
                  onClick={() => onTogglePin(p.id)}
                >
                  <Pin
                    size={12}
                    strokeWidth={2}
                    fill={pinned ? 'currentColor' : 'none'}
                    aria-hidden
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}

/**
 * The number carries the item at both rail widths — it is the identity on a job
 * site — so it holds the icon slot and the description is the label that fades.
 * No "PCO " prefix any more: the section eyebrow above already says Recent
 * PCOs, and a prefix would have to collapse to zero width to keep the number
 * centred in the icon rail.
 *
 * The pin toggle is a SIBLING of the NavLink, absolutely positioned into the
 * row's corner — a button inside an anchor is invalid HTML and splits the
 * click target. Hover/focus-revealed for unpinned rows, always visible when
 * pinned, and gone entirely (display, not opacity) on the icon rail so it
 * never holds a tab stop the collapsed rail can't show.
 */
function PcoRailItem({
  pco,
  onNavigate,
  pinned = false,
  onTogglePin,
}: {
  pco: SettingsPco;
  onNavigate?: () => void;
  pinned?: boolean;
  onTogglePin?: () => void;
}) {
  const num = formatPcoNumber(pco.pco_number);
  return (
    <li>
      <NavLink
        to={`/pcos/${pco.id}`}
        onClick={onNavigate}
        className={({ isActive }) => (isActive ? 'active' : '')}
        title={`PCO ${num} — ${pco.description}`}
      >
        <span className="pco-num">{num}</span>
        <span className="pco-desc rail-label">{pco.description}</span>
      </NavLink>
      {onTogglePin && (
        <button
          type="button"
          className={pinned ? 'rail-pin-toggle is-pinned' : 'rail-pin-toggle'}
          title={pinned ? `Unpin PCO ${num}` : `Pin PCO ${num} to the top of the rail`}
          aria-label={pinned ? `Unpin PCO ${num}` : `Pin PCO ${num} to the top of the rail`}
          aria-pressed={pinned}
          onClick={onTogglePin}
        >
          <Pin size={12} strokeWidth={2} fill={pinned ? 'currentColor' : 'none'} aria-hidden />
        </button>
      )}
    </li>
  );
}

function initial(displayName: string): string {
  const first = displayName.split(/\s+/).filter(Boolean)[0];
  return first?.[0]?.toUpperCase() ?? '?';
}
